/* eslint-disable statura/no-floating-point-money -- Cert v2 Layer-1 oracle: this module INTENTIONALLY mirrors the award engines' float + round2 arithmetic so its output can be diffed against the engines dollar-for-dollar (enforced by eba-cert-parity.test.ts). Integer-cent arithmetic would round differently from the engines and break parity — the whole point of the oracle. */
import type { AllowanceBasis, AllowanceLine, PenaltyBand, RateContext } from '../award-rate-lookup';
import { backOutRoundingUnits, roundMoney, sumMoney } from '../money-rounding';
import { AGED_CARE_MEAL_OT_EMISSION } from '../award-data/aged-care';
import { NURSES_MEAL_OT_EMISSION } from '../award-data/nurses';
import {
  EBA_COMPILE_PROFILES,
  type CompiledEbaOverlay,
  type CompiledOvertimeRule,
  type CompiledPenaltyLine,
  type CompileProfile,
} from './eba-block-compiler';
import {
  conditionalPenaltyBlockCodeForKind,
  findConditionalPenaltyLine,
  resolveCategoryScopedShiftLoading,
  resolveShiftLoading,
  hasGeneralStaffDailyLoadedOvertime,
  hhmmToMinutes,
  type ResolvedShiftLoading,
  timeInWindow,
} from './eba-conditional-penalties';

// 1.0.7 — GS cl 32(c)(i) / Nurses cl 22.22 overtime on the loaded shift rate
// when compiled `overtimeOnLoadedRate` is set (8 Sep M1/M3).
// 1.0.9 — honour includesCasualLoading in effectivePenaltyMultiplier (P45).
// 1.0.10 — resolveShiftLoadingPremiumFromContext honours includesCasualLoading
// before subtracting ordinary (casual night OT loaded-rate premium).
// 1.0.10 — P60: includesCasualLoading true means already loaded; BASE_PLUS base
// rows (flag false/omitted) receive one engine loading pass in
// effectivePenaltyMultiplier.
// 1.0.12 — 2026-09-17 Nurses EA re-derivation: an OT fragment in a shift-
// loading band no longer refuses (SHIFT_LOADING_OVERTIME_SUBSTITUTION_
// NOT_L1_VERIFIABLE) when the compiled overtime rule is not on the loaded
// rate; it prices with zero shift-loading premium instead, matching the
// engine's retainShiftLoadingOnOvertimeForProfile (already false for both
// MA000018 and MA000034).
// 1.0.13 — persisted GS daily pair uses additive premiums and ordinary-only shift loading.
// 1.0.17 — 2026-10-08 half-cent rounding: round2 mirrors the engines' shared exact
// half-up roundMoney (money-rounding.ts) so parity holds on exact half-cents.
// 1.0.18 — 2026-10-08 Astra RND-04: the weekday overtime back-out rounds the
// remaining ordinary pay once per rounding unit (backOutRoundingUnits), mirroring the engines.
export const EBA_CERT_REDUCER_VERSION = '1.0.18';

export type CertDayType = 'weekday' | 'saturday' | 'sunday' | 'publicHoliday';
export type CertEmployment = 'PERMANENT' | 'CASUAL';
export type CertShiftBand =
  | 'WEEKDAY_DAY'
  | 'WEEKDAY_AFTERNOON'
  | 'WEEKDAY_NIGHT'
  | 'PERMANENT_NIGHT_SHIFT';

export interface CertShiftInput {
  baseAwardCode: string;
  baseHourlyRate: number;
  /** Catalogue representation; only the persisted GS additive weekday contract normalises this. */
  baseRateIncludesCasualLoading?: boolean;
  hoursWorked: number;
  dayType: CertDayType;
  employment: CertEmployment;
  ordinaryHoursLimit: number;
  classificationStream?: string | null;
  shiftBand?: CertShiftBand | null;
  allowanceUnits?: Record<string, number> | null;
  conditionalPenaltyCode?: string | null;
  shiftStartTime?: string | null;
  /** Shift finish time (HH:MM, tenant tz) — needed for SPAN / FINISH windows. */
  shiftEndTime?: string | null;
  /** Whole-shift clocks override fragment clocks for loading eligibility. */
  shiftEnvelopeStartTime?: string | null;
  shiftEnvelopeEndTime?: string | null;
  /** Raw employee category — drives the category-scoped shift-loading path. */
  employeeCategory?: string | null;
  missedMealBreak?: boolean | null;
  continuousHoursWithoutMealBreak?: number | null;
  interruptedMealBreak?: boolean | null;
  interruptedMealBreakMinutes?: number | null;
  publicHolidayNotWorked?: boolean | null;
  usualRosteredHours?: number | null;
  /**
   * Optional ordinary-hour split for EBA contexts that pay weekend/PH dollars
   * by worked day boundary while retaining a dominant dayType for stack guards.
   */
  dayTypeHours?: Partial<Record<CertDayType, number>> | null;
  mealProvidedDuringOT?: boolean | null;
  couldReturnHomeForMeal?: boolean | null;
  /**
   * Tier-1 (first 2h @ 150%) OT hours already consumed by an earlier
   * fragment of the SAME shift (e.g. a prior settlement/recompute pass).
   * Mirrors `ShiftPayWithOvertimeInput.overtimeInitialTierHoursConsumedEarlierThisShift`
   * on the award engines so the cert oracle stays coherent with the engine
   * for identical inputs (17 Sep loaded-rate tier-1 consumption fix).
   */
  overtimeInitialTierHoursConsumedEarlierThisShift?: number | null;
  /** Earlier entries on the local date, consumed only by loaded GS daily-pair OT. */
  overtimeInitialTierHoursConsumedEarlierToday?: number | null;
  automaticMealOvertimeAllowance?: {
    firstMealThresholdHours: number;
    secondMealThresholdHours: number;
    firstMealAmount: number;
    secondMealAmount: number;
  } | null;
}

export interface ExpectedPayBreakdown {
  basePay: number;
  penaltyPay: number;
  overtimePay: number;
  shiftLoadingPay: number;
  allowancesPay: number;
  grossTotal: number;
  coveredBands: string[];
  uncoveredReason?: string;
}

export const EBA_CERT_REDUCER_ORDINARY_PENALTY_BANDS = [
  'SATURDAY',
  'SUNDAY',
  'PUBLIC_HOLIDAY',
] as const satisfies readonly PenaltyBand[];

export const EBA_CERT_REDUCER_SHIFT_LOADING_BANDS = [
  'WEEKDAY_AFTERNOON',
  'WEEKDAY_NIGHT',
  'PERMANENT_NIGHT_SHIFT',
] as const satisfies readonly PenaltyBand[];

const DAY_TYPE_BAND: Partial<Record<CertDayType, PenaltyBand>> = {
  saturday: 'SATURDAY',
  sunday: 'SUNDAY',
  publicHoliday: 'PUBLIC_HOLIDAY',
};

const BAND_ORDER: string[] = [
  'WEEKDAY_DAY',
  'SATURDAY',
  'SUNDAY',
  'PUBLIC_HOLIDAY',
  'WEEKDAY_AFTERNOON',
  'WEEKDAY_NIGHT',
  'PERMANENT_NIGHT_SHIFT',
  'OVERTIME_FIRST_2H',
  'OVERTIME_AFTER_2H',
];

const SUPPORTED_ALLOWANCE_BASIS = new Set<AllowanceBasis>([
  'PER_SHIFT',
  'PER_KM',
  'PER_DAY',
  'PER_HOUR',
  'PER_INCIDENT',
]);

function round2(value: number): number {
  return roundMoney(value);
}

function zeroBreakdown(reason: string, coveredBands: string[] = []): ExpectedPayBreakdown {
  return {
    basePay: 0,
    penaltyPay: 0,
    overtimePay: 0,
    shiftLoadingPay: 0,
    allowancesPay: 0,
    grossTotal: 0,
    coveredBands: sortBands(coveredBands),
    uncoveredReason: reason,
  };
}

function sortBands(bands: Iterable<string>): string[] {
  return [...new Set(bands)].sort((a, b) => {
    const ai = BAND_ORDER.indexOf(a);
    const bi = BAND_ORDER.indexOf(b);
    const ao = ai === -1 ? BAND_ORDER.length : ai;
    const bo = bi === -1 ? BAND_ORDER.length : bi;
    return ao - bo || a.localeCompare(b);
  });
}

function profileFor(baseAwardCode: string): CompileProfile | null {
  return EBA_COMPILE_PROFILES[baseAwardCode] ?? null;
}

function casualMultiplier(profile: CompileProfile, employment: CertEmployment): number {
  // Casual convention is inherited from the base award profile:
  // MA000018 cl 10.4(b) uses inclusive casual penalty multipliers; MA000034
  // cl 19.2/21/28 use the casual hourly rate, so the reducer applies the
  // 25% loading separately where the compiled EBA penalty lines carry the
  // permanent multiplier.
  return employment === 'CASUAL' ? 1 + profile.casualLoadingRate : 1;
}

function findCompiledPenaltyLine(
  overlay: CompiledEbaOverlay,
  band: PenaltyBand,
  employment: CertEmployment,
  classificationStream?: string | null,
): CompiledPenaltyLine | null {
  const specific = classificationStream
    ? overlay.penalties.find(
        (line) =>
          line.band === band &&
          line.employmentType === employment &&
          line.classificationStream === classificationStream,
      )
    : undefined;
  if (specific) return specific;

  return (
    overlay.penalties.find(
      (line) =>
        line.band === band &&
        line.employmentType === employment &&
        (line.classificationStream ?? null) === null,
    ) ?? null
  );
}

function findPenaltyMultiplier(
  overlay: CompiledEbaOverlay,
  band: PenaltyBand,
  employment: CertEmployment,
  classificationStream?: string | null,
): number | null {
  return (
    findCompiledPenaltyLine(overlay, band, employment, classificationStream)?.multiplier ?? null
  );
}

function findAllowance(
  allowances: readonly AllowanceLine[],
  code: string,
  employment: CertEmployment,
): AllowanceLine | null {
  const specific = allowances.find(
    (line) => line.allowanceCode === code && line.appliesToEmployment === employment,
  );
  if (specific) return specific;
  return (
    allowances.find(
      (line) => line.allowanceCode === code && (line.appliesToEmployment ?? null) === null,
    ) ?? null
  );
}

function effectivePenaltyMultiplier(
  profile: CompileProfile,
  employment: CertEmployment,
  compiledMultiplier: number,
  includesCasualLoading?: boolean,
): number {
  if (employment !== 'CASUAL') return compiledMultiplier;
  if (includesCasualLoading === true) {
    return compiledMultiplier;
  }
  if (profile.casualPenaltyConvention === 'BASE_PLUS_ENGINE_LOADING') {
    return compiledMultiplier * (1 + profile.casualLoadingRate);
  }
  return compiledMultiplier;
}

function isCoveredShiftLoadingBand(
  profile: CompileProfile,
  band: CertShiftBand | null | undefined,
): band is Extract<CertShiftBand, 'WEEKDAY_AFTERNOON' | 'WEEKDAY_NIGHT' | 'PERMANENT_NIGHT_SHIFT'> {
  return (
    (band === 'WEEKDAY_AFTERNOON' ||
      band === 'WEEKDAY_NIGHT' ||
      band === 'PERMANENT_NIGHT_SHIFT') &&
    profile.ctxReadableBands.includes(band)
  );
}

function structuralReasonForInput(
  overlay: CompiledEbaOverlay,
  input: CertShiftInput,
): string | null {
  if (
    (input.dayType === 'saturday' || input.dayType === 'sunday') &&
    overlay.structuralUnsupported.some(
      (block) => block.reason === 'WEEKEND_AS_OVERTIME_REQUIRES_ENGINE_INTERPRETER',
    )
  ) {
    return 'STRUCTURAL_UNSUPPORTED:WEEKEND_AS_OVERTIME_REQUIRES_ENGINE_INTERPRETER';
  }

  if (
    (input.shiftBand === 'WEEKDAY_AFTERNOON' || input.shiftBand === 'WEEKDAY_NIGHT') &&
    overlay.structuralUnsupported.some(
      (block) => block.reason === 'AGED_CARE_COMMENCEMENT_SPAN_REBUILD_REQUIRED',
    )
  ) {
    return 'STRUCTURAL_UNSUPPORTED:AGED_CARE_COMMENCEMENT_SPAN_REBUILD_REQUIRED';
  }

  return null;
}

function manualAllowanceReason(
  overlay: CompiledEbaOverlay,
  allowanceUnits: Record<string, number>,
): string | null {
  for (const [code, units] of Object.entries(allowanceUnits)) {
    if (!Number.isFinite(units) || units <= 0) continue;
    const contract = overlay.manualContracts.find((manual) =>
      manual.requiredAllowanceCodes.includes(code),
    );
    if (contract) {
      return `MANUAL_ALLOWANCE_CONTRACT_NOT_L1_VERIFIABLE:${code}:${contract.blockCode}`;
    }
  }
  return null;
}

function allowanceAmount(line: AllowanceLine, units: number): number {
  return round2(line.amount * units);
}

function resolveAllowancesPay(
  overlay: CompiledEbaOverlay,
  input: CertShiftInput,
): { allowancesPay: number } | { uncoveredReason: string } {
  const allowanceUnits = input.allowanceUnits ?? {};
  const manualReason = manualAllowanceReason(overlay, allowanceUnits);
  if (manualReason) return { uncoveredReason: manualReason };

  let allowancesPay = 0;
  for (const [code, units] of Object.entries(allowanceUnits)) {
    if (!Number.isFinite(units) || units <= 0) continue;
    const line = findAllowance(overlay.allowances, code, input.employment);
    if (!line) return { uncoveredReason: `ALLOWANCE_LINE_MISSING:${code}` };
    if (!SUPPORTED_ALLOWANCE_BASIS.has(line.basis)) {
      return { uncoveredReason: `ALLOWANCE_BASIS_NOT_L1_VERIFIABLE:${code}:${line.basis}` };
    }
    allowancesPay = round2(allowancesPay + allowanceAmount(line, units));
  }

  return { allowancesPay };
}

export function primaryOvertimeRule(overlay: CompiledEbaOverlay): CompiledOvertimeRule | null {
  return overlay.overtimeRules[0] ?? null;
}

export function overtimeOnLoadedRateEnabled(overlay: CompiledEbaOverlay): boolean {
  return overlay.overtimeRules.some((rule) => rule.overtimeOnLoadedRate);
}

export function primaryOvertimeRuleFromContext(ctx: RateContext): CompiledOvertimeRule | null {
  return ctx.overtimeRules?.[0] ?? null;
}

export function overtimeOnLoadedRateEnabledFromContext(ctx: RateContext): boolean {
  return (ctx.overtimeRules ?? []).some((rule) => rule.overtimeOnLoadedRate);
}

function compileProfileForContext(ctx: RateContext): CompileProfile | null {
  const awardCode = ctx.baseAwardCode ?? ctx.awardCode;
  return EBA_COMPILE_PROFILES[awardCode] ?? null;
}

export function shiftLoadingPremiumFromMultiplier(
  profile: CompileProfile,
  employment: CertEmployment,
  totalMultiplier: number,
): number {
  const ordinaryMultiplier = conditionalOrdinaryMultiplier(profile, employment);
  return Math.max(0, totalMultiplier - ordinaryMultiplier);
}

function dailyPairLoadingForInput(
  overlay: CompiledEbaOverlay,
  profile: CompileProfile,
  input: CertShiftInput,
): ResolvedShiftLoading {
  const start =
    input.shiftEnvelopeStartTime ??
    input.shiftStartTime ??
    (input.shiftBand === 'WEEKDAY_AFTERNOON'
      ? '14:00'
      : input.shiftBand === 'WEEKDAY_NIGHT' || input.shiftBand === 'PERMANENT_NIGHT_SHIFT'
        ? '22:00'
        : '07:00');
  const finishMinutes = ((hhmmToMinutes(start) ?? 420) + Math.round(input.hoursWorked * 60)) % 1440;
  const inferredFinish = `${String(Math.floor(finishMinutes / 60)).padStart(2, '0')}:${String(finishMinutes % 60).padStart(2, '0')}`;
  return resolveShiftLoading(
    overlay,
    input.employment,
    {
      employeeCategory: input.employeeCategory,
      dayType: input.dayType,
      shiftStartTime: start,
      shiftEndTime: input.shiftEnvelopeEndTime ?? input.shiftEndTime ?? inferredFinish,
    },
    profile.casualLoadingRate,
  );
}

function shiftLoadingPremiumForInput(
  overlay: CompiledEbaOverlay,
  profile: CompileProfile,
  input: CertShiftInput,
): number | null {
  if (input.shiftBand && input.shiftBand !== 'WEEKDAY_DAY') {
    const compiledLine = findCompiledPenaltyLine(
      overlay,
      input.shiftBand,
      input.employment,
      input.classificationStream,
    );
    if (compiledLine === null) return null;
    return shiftLoadingPremiumFromMultiplier(
      profile,
      input.employment,
      effectivePenaltyMultiplier(
        profile,
        input.employment,
        compiledLine.multiplier,
        compiledLine.includesCasualLoading,
      ),
    );
  }

  if (input.shiftStartTime && input.dayType === 'weekday' && input.conditionalPenaltyCode) {
    const line = findConditionalPenaltyLine(overlay, 'SHIFT_START_WINDOW', input.employment, {
      blockCode: input.conditionalPenaltyCode,
      shiftStartTime: input.shiftStartTime,
    });
    if (!line) return null;
    if (line.startWindow && !timeInWindow(input.shiftStartTime, line.startWindow)) return 0;
    return shiftLoadingPremiumFromMultiplier(profile, input.employment, line.multiplier);
  }

  return 0;
}

function hasShiftLoadingOvertimeContext(input: CertShiftInput): boolean {
  return (
    (input.shiftBand !== undefined &&
      input.shiftBand !== null &&
      input.shiftBand !== 'WEEKDAY_DAY') ||
    (input.shiftStartTime !== undefined &&
      input.shiftStartTime !== null &&
      input.conditionalPenaltyCode !== undefined &&
      input.conditionalPenaltyCode !== null &&
      input.dayType === 'weekday')
  );
}

/** Map OT hours to 150%/200% export buckets from multipliers actually used. */
export function otExportTierHours(
  overtimeHours: number,
  tier1Mult: number,
  tier2Mult: number,
  tier1Budget = 2,
): { otHoursAt15: number; otHoursAt20: number } {
  if (tier1Mult === tier2Mult) {
    return { otHoursAt15: 0, otHoursAt20: overtimeHours };
  }
  const tier1Hours = Math.min(tier1Budget, Math.min(2, overtimeHours));
  return {
    otHoursAt15: tier1Hours,
    otHoursAt20: Math.max(0, overtimeHours - tier1Hours),
  };
}

export function calculateEbaTieredOvertimePay(
  baseHourlyRate: number,
  overtimeHours: number,
  employmentMultiplier: number,
  options?: {
    shiftLoadingPremium?: number;
    onLoadedRate?: boolean;
    shiftLoadingIsAdditive?: boolean;
    firstTwoHoursMultiplier?: number;
    afterTwoHoursMultiplier?: number;
    /**
     * Tier-1 budget (hours) still available to this fragment, after any
     * tier-1 hours an earlier fragment of the same shift already consumed.
     * Defaults to 2 (the full first-2h cl 19.1(a)(i)/25.1 cutover) so every
     * existing caller and pin is unchanged. 17 Sep fix: the loaded-rate
     * weekday-OT branch previously always granted a fresh 2h budget here,
     * ignoring `tier1Remaining` the caller had already tracked.
     */
    tier1HoursRemaining?: number;
  },
): number {
  // OT tier boundaries are clause-derived, not engine-derived:
  // MA000034 cl 19.1(a)(i)/19.2(a)(i) and MA000018 cl 25.1 use 150% for
  // the first 2 hours and 200% thereafter for weekday single-shift OT.
  const tier1Mult = options?.firstTwoHoursMultiplier ?? 1.5;
  const tier2Mult = options?.afterTwoHoursMultiplier ?? 2.0;
  const shiftLoadingPremium = options?.onLoadedRate ? (options.shiftLoadingPremium ?? 0) : 0;
  const hourlyBase = baseHourlyRate * (1 + shiftLoadingPremium);
  const tier1Budget = options?.tier1HoursRemaining ?? 2;
  const tier1Hours = Math.min(tier1Budget, overtimeHours);
  const tier2Hours = Math.max(0, overtimeHours - tier1Hours);
  if (options?.shiftLoadingIsAdditive) {
    const additiveBase = baseHourlyRate * (employmentMultiplier + shiftLoadingPremium);
    return round2(tier1Hours * additiveBase * tier1Mult + tier2Hours * additiveBase * tier2Mult);
  }
  return round2(
    (tier1Hours * hourlyBase * tier1Mult + tier2Hours * hourlyBase * tier2Mult) *
      employmentMultiplier,
  );
}

/** @deprecated Use calculateEbaTieredOvertimePay — shared by cert reducer and award engines. */
function overtimePay(
  baseHourlyRate: number,
  overtimeHours: number,
  employmentMultiplier: number,
  options?: Parameters<typeof calculateEbaTieredOvertimePay>[3],
): number {
  return calculateEbaTieredOvertimePay(
    baseHourlyRate,
    overtimeHours,
    employmentMultiplier,
    options,
  );
}

/** Map source-evidence / conditional-penalty block codes to a shift-loading band. */
export function inferShiftBandFromConditionalPenaltyCode(
  code: string | null | undefined,
): CertShiftBand | null {
  if (!code) return null;
  const normalized = code.trim().toUpperCase();
  if (normalized.includes('PERMANENT_NIGHT')) return 'PERMANENT_NIGHT_SHIFT';
  if (normalized.includes('NIGHT')) return 'WEEKDAY_NIGHT';
  if (normalized.includes('AFTERNOON') || normalized.includes('EVENING')) {
    return 'WEEKDAY_AFTERNOON';
  }
  return null;
}

export function resolveShiftLoadingPremiumFromContext(input: {
  ctx: RateContext;
  employment: CertEmployment;
  employeeCategory?: string | null;
  shiftBand?: CertShiftBand | null;
  shiftStartTime?: string | null;
  conditionalPenaltyCode?: string | null;
  dayType?: CertDayType;
}): number | null {
  const profile = compileProfileForContext(input.ctx);
  if (!profile) return 0;

  if (input.shiftBand && input.shiftBand !== 'WEEKDAY_DAY') {
    const line = input.ctx.penalties.find(
      (penalty) => penalty.band === input.shiftBand && penalty.employmentType === input.employment,
    );
    if (!line) return null;
    return shiftLoadingPremiumFromMultiplier(
      profile,
      input.employment,
      effectivePenaltyMultiplier(
        profile,
        input.employment,
        line.multiplier,
        line.includesCasualLoading,
      ),
    );
  }

  if (input.shiftStartTime && input.dayType === 'weekday' && input.conditionalPenaltyCode) {
    const line = findConditionalPenaltyLine(input.ctx, 'SHIFT_START_WINDOW', input.employment, {
      blockCode: input.conditionalPenaltyCode,
      shiftStartTime: input.shiftStartTime,
      employeeCategory: input.employeeCategory,
    });
    if (!line) return null;
    if (line.startWindow && !timeInWindow(input.shiftStartTime, line.startWindow)) return 0;
    return shiftLoadingPremiumFromMultiplier(
      profile,
      input.employment,
      effectivePenaltyMultiplier(
        profile,
        input.employment,
        line.multiplier,
        line.includesCasualLoading,
      ),
    );
  }

  return 0;
}

const MEAL_OT_BY_AWARD = {
  MA000018: AGED_CARE_MEAL_OT_EMISSION,
  MA000034: NURSES_MEAL_OT_EMISSION,
} as const;

function automaticMealOvertimeAllowancePay(input: CertShiftInput, overtimeHours: number): number {
  const mealOt =
    input.automaticMealOvertimeAllowance === undefined
      ? MEAL_OT_BY_AWARD[input.baseAwardCode as keyof typeof MEAL_OT_BY_AWARD]
      : input.automaticMealOvertimeAllowance;
  if (!mealOt) return 0;

  // Automatic meal OT is award-data-driven and deliberately separate from
  // compiled EBA allowance rows while Phase 1 defers allowance emission.
  // TODO: when compileEbaOverlay starts materialising allowance lines, keep
  // this reducer path and compiler allowance emission in lockstep so MEAL /
  // MEAL_OT_ADDITIONAL are neither dropped nor double counted.
  //
  // MA000034 cl 17.3(b)(i)-(ii) and MA000018 cl 15.4(a)(i)-(ii):
  // first PER_INCIDENT meal at $16.62 when OT exceeds 1h past usual finish;
  // further PER_INCIDENT meal at $14.98 when cumulative OT exceeds 4h.
  // Opt-outs are exceptional and default false: meal supplied during OT, or
  // worker could reasonably return home for the meal.
  const mealCarveOut =
    (input.mealProvidedDuringOT ?? false) || (input.couldReturnHomeForMeal ?? false);
  if (mealCarveOut || overtimeHours <= mealOt.firstMealThresholdHours) return 0;

  let pay: number = mealOt.firstMealAmount;
  if (overtimeHours > mealOt.secondMealThresholdHours) {
    pay = round2(pay + mealOt.secondMealAmount);
  }
  return pay;
}

function conditionalOrdinaryMultiplier(
  profile: CompileProfile,
  employment: CertEmployment,
): number {
  return employment === 'CASUAL' ? 1 + profile.casualLoadingRate : 1;
}

function conditionalPenaltyExtraPay(input: {
  baseHourlyRate: number;
  hours: number;
  totalMultiplier: number;
  ordinaryMultiplier: number;
}): number {
  return round2(
    input.baseHourlyRate *
      Math.max(0, input.hours) *
      Math.max(0, input.totalMultiplier - input.ordinaryMultiplier),
  );
}

function shiftStartWindowHours(input: CertShiftInput): number {
  const segmentedWeekdayHours = input.dayTypeHours?.weekday;
  if (segmentedWeekdayHours === undefined || segmentedWeekdayHours === null) {
    return input.hoursWorked;
  }
  return Math.max(0, segmentedWeekdayHours);
}

function resolveConditionalPenaltyPay(
  overlay: CompiledEbaOverlay,
  profile: CompileProfile,
  input: CertShiftInput,
  dailyLoading?: ResolvedShiftLoading,
):
  | { penaltyPay: number; shiftLoadingPay: number; basePay?: number; covered: string[] }
  | { uncoveredReason: string } {
  const ordinaryMultiplier = conditionalOrdinaryMultiplier(profile, input.employment);
  const blockCode = input.conditionalPenaltyCode ?? null;
  const covered: string[] = [];
  let penaltyPay = 0;
  let shiftLoadingPay = 0;
  let basePay: number | undefined;

  if (input.publicHolidayNotWorked) {
    const publicHolidayNotWorkedBlockCode = conditionalPenaltyBlockCodeForKind(
      overlay,
      'PUBLIC_HOLIDAY_NOT_WORKED',
      blockCode,
    );
    const line = findConditionalPenaltyLine(
      overlay,
      'PUBLIC_HOLIDAY_NOT_WORKED',
      input.employment,
      {
        blockCode: publicHolidayNotWorkedBlockCode,
      },
    );
    if (!line) {
      return {
        uncoveredReason: `CONDITIONAL_PENALTY_LINE_MISSING:PUBLIC_HOLIDAY_NOT_WORKED:${input.employment}`,
      };
    }
    const hours = input.usualRosteredHours ?? input.hoursWorked;
    basePay = round2(hours * input.baseHourlyRate * line.multiplier);
    covered.push(line.blockCode);
  }

  // Mirrors the engine (aged-care.ts): a new-style, category-scoped overlay
  // resolves the shift loading DETERMINISTICALLY from the shift times + employee
  // category (windowMode drives COMMENCEMENT / SPAN / FINISH); a legacy overlay
  // keeps the blockCode-gated commencement path. Provider-agnostic — the same
  // resolution serves any EA's blocks.
  const ebaCategoryScopedShiftLoading = (overlay.conditionalPenalties ?? []).some(
    (line) =>
      line.kind === 'SHIFT_START_WINDOW' &&
      ((line.employeeCategories?.length ?? 0) > 0 || line.windowMode != null),
  );

  if (dailyLoading && input.dayType === 'weekday') {
    if (dailyLoading.unclassified)
      return { uncoveredReason: 'EBA_SHIFT_LOADING_EMPLOYEE_CATEGORY_UNCLASSIFIED' };
    const hours = Math.min(shiftStartWindowHours(input), input.ordinaryHoursLimit);
    shiftLoadingPay = round2(input.baseHourlyRate * hours * dailyLoading.premium);
    if (dailyLoading.line) covered.push(dailyLoading.line.blockCode);
  } else if (input.shiftStartTime && input.dayType === 'weekday') {
    const overtimeRule = primaryOvertimeRule(overlay);
    const loadingHours =
      overtimeRule?.overtimeOnLoadedRate && overtimeRule.shiftLoadingIsAdditive
        ? Math.min(shiftStartWindowHours(input), input.ordinaryHoursLimit)
        : shiftStartWindowHours(input);
    if (ebaCategoryScopedShiftLoading) {
      // Same shared resolver the engine uses → identical fail-closed decision.
      // Unclassified (null/empty or unrecognised category) AND recognised-but-no-
      // window-match both yield $0 shift loading, which mirrors the engine.
      const resolved = resolveCategoryScopedShiftLoading(overlay, input.employment, {
        employeeCategory: input.employeeCategory,
        shiftStartTime: input.shiftStartTime,
        shiftEndTime: input.shiftEndTime ?? null,
      });
      if (resolved.line) {
        shiftLoadingPay = round2(
          shiftLoadingPay +
            conditionalPenaltyExtraPay({
              baseHourlyRate: input.baseHourlyRate,
              hours: loadingHours,
              totalMultiplier: resolved.line.multiplier,
              ordinaryMultiplier,
            }),
        );
        covered.push(resolved.line.blockCode);
      }
    } else if (blockCode) {
      const line = findConditionalPenaltyLine(overlay, 'SHIFT_START_WINDOW', input.employment, {
        blockCode,
        shiftStartTime: input.shiftStartTime,
      });
      if (!line) {
        return {
          uncoveredReason: `CONDITIONAL_PENALTY_LINE_MISSING:SHIFT_START_WINDOW:${input.employment}:${blockCode}`,
        };
      }
      if (line.startWindow && !timeInWindow(input.shiftStartTime, line.startWindow)) {
        return { uncoveredReason: `CONDITIONAL_SHIFT_START_OUTSIDE_WINDOW:${line.blockCode}` };
      }
      shiftLoadingPay = round2(
        shiftLoadingPay +
          conditionalPenaltyExtraPay({
            baseHourlyRate: input.baseHourlyRate,
            hours: loadingHours,
            totalMultiplier: line.multiplier,
            ordinaryMultiplier,
          }),
      );
      covered.push(line.blockCode);
    }
  }

  if (input.missedMealBreak) {
    const mealBreakBlockCode = conditionalPenaltyBlockCodeForKind(overlay, 'MEAL_BREAK', blockCode);
    const line = findConditionalPenaltyLine(overlay, 'MEAL_BREAK', input.employment, {
      blockCode: mealBreakBlockCode,
    });
    if (!line) {
      return {
        uncoveredReason: `CONDITIONAL_PENALTY_LINE_MISSING:MEAL_BREAK:${input.employment}`,
      };
    }
    const trigger = line.triggerContinuousHours ?? 5;
    const continuousHours = input.continuousHoursWithoutMealBreak ?? input.hoursWorked;
    penaltyPay = round2(
      penaltyPay +
        conditionalPenaltyExtraPay({
          baseHourlyRate: input.baseHourlyRate,
          hours: Math.max(0, continuousHours - trigger),
          totalMultiplier: line.multiplier,
          ordinaryMultiplier,
        }),
    );
    covered.push(line.blockCode);
  }

  if (input.interruptedMealBreak) {
    const interruptedMealBreakBlockCode = conditionalPenaltyBlockCodeForKind(
      overlay,
      'INTERRUPTED_MEAL_BREAK',
      blockCode,
    );
    const line = findConditionalPenaltyLine(overlay, 'INTERRUPTED_MEAL_BREAK', input.employment, {
      blockCode: interruptedMealBreakBlockCode,
    });
    if (!line) {
      return {
        uncoveredReason: `CONDITIONAL_PENALTY_LINE_MISSING:INTERRUPTED_MEAL_BREAK:${input.employment}`,
      };
    }
    const hours = (input.interruptedMealBreakMinutes ?? 30) / 60;
    penaltyPay = round2(
      penaltyPay +
        conditionalPenaltyExtraPay({
          baseHourlyRate: input.baseHourlyRate,
          hours,
          totalMultiplier: line.multiplier,
          ordinaryMultiplier,
        }),
    );
    covered.push(line.blockCode);
  }

  return { penaltyPay, shiftLoadingPay, basePay, covered };
}

function normaliseDayTypeHours(
  input: CertShiftInput,
  ordinaryHours: number,
): { hours: Record<CertDayType, number> | null } | { uncoveredReason: string } {
  if (!input.dayTypeHours) return { hours: null };

  const hours: Record<CertDayType, number> = {
    weekday: 0,
    saturday: 0,
    sunday: 0,
    publicHoliday: 0,
  };

  for (const dayType of Object.keys(hours) as CertDayType[]) {
    const value = input.dayTypeHours[dayType] ?? 0;
    if (!Number.isFinite(value) || value < 0) {
      return { uncoveredReason: `INVALID_DAY_TYPE_HOURS:${dayType}` };
    }
    hours[dayType] = value;
  }

  const total = Object.values(hours).reduce((sum, value) => sum + value, 0);
  if (Math.abs(total - ordinaryHours) > 0.001) {
    return { uncoveredReason: 'DAY_TYPE_HOURS_DO_NOT_MATCH_ORDINARY_HOURS' };
  }

  return { hours };
}

/** Mirror existing MA000018 weekend catalogue normalization, without changing
 * engine weekend money. Public holidays deliberately retain their existing basis. */
function ordinaryPenaltyBase(
  input: CertShiftInput,
  band: PenaltyBand,
  overlay: CompiledEbaOverlay,
  profile: CompileProfile,
): number {
  return input.baseAwardCode === 'MA000018' &&
    hasGeneralStaffDailyLoadedOvertime(overlay) &&
    input.employment === 'CASUAL' &&
    input.baseRateIncludesCasualLoading === true &&
    (band === 'SATURDAY' || band === 'SUNDAY')
    ? Math.round((input.baseHourlyRate / (1 + profile.casualLoadingRate)) * 10000) / 10000
    : input.baseHourlyRate;
}

function ordinaryPenaltyPayForHours(input: {
  overlay: CompiledEbaOverlay;
  profile: CompileProfile;
  source: CertShiftInput;
  band: PenaltyBand;
  hours: number;
}): { pay: number; covered: boolean } | { uncoveredReason: string } {
  if (input.hours <= 0) return { pay: 0, covered: false };
  const compiledLine = findCompiledPenaltyLine(
    input.overlay,
    input.band,
    input.source.employment,
    input.source.classificationStream,
  );
  if (compiledLine === null) {
    return {
      uncoveredReason: `PENALTY_LINE_MISSING:${input.band}:${input.source.employment}`,
    };
  }
  const multiplier = effectivePenaltyMultiplier(
    input.profile,
    input.source.employment,
    compiledLine.multiplier,
    compiledLine.includesCasualLoading,
  );
  return {
    pay: round2(
      input.hours *
        ordinaryPenaltyBase(input.source, input.band, input.overlay, input.profile) *
        multiplier,
    ),
    covered: true,
  };
}

export function isEbaCertReducerBandInScope(baseAwardCode: string, band: string): boolean {
  const profile = profileFor(baseAwardCode);
  if (!profile) return false;
  if (
    EBA_CERT_REDUCER_ORDINARY_PENALTY_BANDS.includes(
      band as (typeof EBA_CERT_REDUCER_ORDINARY_PENALTY_BANDS)[number],
    )
  ) {
    return profile.ctxReadableBands.includes(band as PenaltyBand);
  }
  if (
    EBA_CERT_REDUCER_SHIFT_LOADING_BANDS.includes(
      band as (typeof EBA_CERT_REDUCER_SHIFT_LOADING_BANDS)[number],
    )
  ) {
    return (
      profile.baseAwardCode === 'MA000034' && profile.ctxReadableBands.includes(band as PenaltyBand)
    );
  }
  return band === 'WEEKDAY_DAY';
}

export function ebaCertReducerBandUncoveredReason(baseAwardCode: string, band: string): string {
  const profile = profileFor(baseAwardCode);
  if (!profile) return `NO_COMPILE_PROFILE_FOR_${baseAwardCode}`;
  if (band === 'OVERTIME_FIRST_2H' || band === 'OVERTIME_AFTER_2H') {
    return 'CUSTOM_OVERTIME_BANDS_NOT_CTX_READABLE';
  }
  if (
    profile.baseAwardCode === 'MA000018' &&
    (band === 'WEEKDAY_AFTERNOON' || band === 'WEEKDAY_NIGHT')
  ) {
    return 'AGED_CARE_COMMENCEMENT_SPAN_REBUILD_REQUIRED';
  }
  if (!profile.ctxReadableBands.includes(band as PenaltyBand) && band !== 'WEEKDAY_DAY') {
    return `PENALTY_BAND_NOT_CTX_READABLE:${band}`;
  }
  return `BAND_NOT_L1_VERIFIABLE:${band}`;
}

export function reduceEbaExpectedPay(
  overlay: CompiledEbaOverlay,
  input: CertShiftInput,
): ExpectedPayBreakdown {
  const profile = profileFor(input.baseAwardCode);
  if (!profile) return zeroBreakdown(`NO_COMPILE_PROFILE_FOR_${input.baseAwardCode}`);
  if (!Number.isFinite(input.baseHourlyRate) || input.baseHourlyRate < 0) {
    return zeroBreakdown('INVALID_BASE_HOURLY_RATE');
  }
  if (!Number.isFinite(input.hoursWorked) || input.hoursWorked < 0) {
    return zeroBreakdown('INVALID_HOURS_WORKED');
  }
  if (!Number.isFinite(input.ordinaryHoursLimit) || input.ordinaryHoursLimit < 0) {
    return zeroBreakdown('INVALID_ORDINARY_HOURS_LIMIT');
  }

  const dailyPair =
    input.baseAwardCode === 'MA000018' && hasGeneralStaffDailyLoadedOvertime(overlay);
  // Resolve once, before catalogue normalization; premium and eligibility do
  // not depend on how the base hourly rate is represented.
  const dailyLoading = dailyPair ? dailyPairLoadingForInput(overlay, profile, input) : undefined;
  if (
    dailyPair &&
    input.employment === 'CASUAL' &&
    input.dayType === 'weekday' &&
    !input.dayTypeHours?.saturday &&
    !input.dayTypeHours?.sunday &&
    !input.dayTypeHours?.publicHoliday &&
    input.baseRateIncludesCasualLoading === true &&
    (dailyLoading?.premium ?? 0) > 0
  ) {
    input = {
      ...input,
      baseHourlyRate: input.baseHourlyRate / (1 + profile.casualLoadingRate),
      baseRateIncludesCasualLoading: false,
    };
  }

  const structuralReason = structuralReasonForInput(overlay, input);
  if (structuralReason) return zeroBreakdown(structuralReason);

  const allowanceResult = resolveAllowancesPay(overlay, input);
  if ('uncoveredReason' in allowanceResult) return zeroBreakdown(allowanceResult.uncoveredReason);
  const conditionalResult = resolveConditionalPenaltyPay(overlay, profile, input, dailyLoading);
  if ('uncoveredReason' in conditionalResult) {
    return zeroBreakdown(conditionalResult.uncoveredReason);
  }

  const totalHours = input.hoursWorked;
  const ordinaryHours = Math.min(totalHours, input.ordinaryHoursLimit);
  const otHours = Math.max(0, totalHours - ordinaryHours);
  const employmentMultiplier =
    input.dayType === 'weekday' &&
    input.employment === 'CASUAL' &&
    input.baseRateIncludesCasualLoading === true &&
    dailyPair
      ? 1
      : casualMultiplier(profile, input.employment);
  const coveredBands = new Set<string>();
  const segmentedDayTypeHours = normaliseDayTypeHours(input, ordinaryHours);
  if ('uncoveredReason' in segmentedDayTypeHours) {
    return zeroBreakdown(segmentedDayTypeHours.uncoveredReason);
  }

  if (otHours > 0 && input.dayType !== 'weekday') {
    return zeroBreakdown('NON_WEEKDAY_OVERTIME_NOT_L1_VERIFIABLE');
  }
  const overtimeRule = primaryOvertimeRule(overlay);
  const overtimeUsesLoadedRate = overtimeOnLoadedRateEnabled(overlay);
  // 2026-09-17 Nurses EA re-derivation: `!overtimeUsesLoadedRate` used to
  // REFUSE (SHIFT_LOADING_OVERTIME_SUBSTITUTION_NOT_L1_VERIFIABLE) whenever
  // OT fell in a shift-loading band, because whether the loading was
  // retained on OT was genuinely unresolved. It no longer is: the Nurses EA
  // artifact confirms the loading is NOT retained on OT (cl 22.2 confines
  // cl 22.1 to ordinary hours; cl 22.3/22.4 are substitutional). A shift
  // band on an OT fragment with no loaded-rate rule now simply prices that
  // fragment with zero shift-loading premium — the same answer the engine
  // gives via `retainShiftLoadingOnOvertimeForProfile` (already false for
  // both MA000018 and MA000034) — instead of refusing.
  // docs/operations/clause-verifications/2026-09-17-aged-care-legislation-medea-nurses-ea-overtime-rate-basis-fix-shape.md
  let shiftLoadingPremiumForOvertime: number | null = 0;
  if (dailyPair) {
    shiftLoadingPremiumForOvertime = dailyLoading!.premium;
  } else if (otHours > 0 && hasShiftLoadingOvertimeContext(input) && overtimeUsesLoadedRate) {
    shiftLoadingPremiumForOvertime = shiftLoadingPremiumForInput(overlay, profile, input);
    if (shiftLoadingPremiumForOvertime === null) {
      const missingBand = input.shiftBand ?? input.conditionalPenaltyCode ?? 'UNKNOWN';
      return zeroBreakdown(`PENALTY_LINE_MISSING:${missingBand}:${input.employment}`);
    }
  }

  let basePay = 0;
  let penaltyPay = 0;
  let shiftLoadingPay = 0;
  let otPay = 0;

  const ordinaryPenaltyBand = DAY_TYPE_BAND[input.dayType];
  if (conditionalResult.basePay !== undefined) {
    basePay = conditionalResult.basePay;
  } else if (segmentedDayTypeHours.hours) {
    const segmentedHours = segmentedDayTypeHours.hours;
    if (segmentedHours.weekday > 0) {
      basePay = round2(segmentedHours.weekday * input.baseHourlyRate * employmentMultiplier);
      coveredBands.add('WEEKDAY_DAY');
    }
    for (const [dayType, band] of Object.entries(DAY_TYPE_BAND) as Array<
      [CertDayType, PenaltyBand]
    >) {
      const hours = segmentedHours[dayType];
      const result = ordinaryPenaltyPayForHours({
        overlay,
        profile,
        source: input,
        band,
        hours,
      });
      if ('uncoveredReason' in result) {
        return zeroBreakdown(result.uncoveredReason, [...coveredBands]);
      }
      penaltyPay = round2(penaltyPay + result.pay);
      if (result.covered) coveredBands.add(band);
    }
  } else if (ordinaryPenaltyBand) {
    const compiledLine = findCompiledPenaltyLine(
      overlay,
      ordinaryPenaltyBand,
      input.employment,
      input.classificationStream,
    );
    if (compiledLine === null) {
      return zeroBreakdown(`PENALTY_LINE_MISSING:${ordinaryPenaltyBand}:${input.employment}`, [
        ...coveredBands,
      ]);
    }
    const multiplier = effectivePenaltyMultiplier(
      profile,
      input.employment,
      compiledLine.multiplier,
      compiledLine.includesCasualLoading,
    );
    penaltyPay = round2(
      ordinaryHours *
        ordinaryPenaltyBase(input, ordinaryPenaltyBand, overlay, profile) *
        multiplier,
    );
    coveredBands.add(ordinaryPenaltyBand);
  } else {
    basePay =
      conditionalResult.basePay ??
      round2(ordinaryHours * input.baseHourlyRate * employmentMultiplier);
    coveredBands.add('WEEKDAY_DAY');
  }

  // Unloaded dayworker money is held to main, including its two-stage
  // ordinary-dollar substitution rounding. Loaded shifts round remaining
  // ordinary hours once under the corrected daily-pair contract.
  // 1.0.18: mirror the engines' non-daily-pair weekday back-out, which rounds
  // the remaining ordinary base once (backOutRoundingUnits) so exact half-cents
  // stay in parity.
  if (
    (!dailyPair || dailyLoading!.premium === 0) &&
    conditionalResult.basePay === undefined &&
    otHours > 0 &&
    input.dayType === 'weekday' &&
    !input.dayTypeHours?.saturday &&
    !input.dayTypeHours?.sunday &&
    !input.dayTypeHours?.publicHoliday
  ) {
    // Astra RND-04/07: the engines back out per rounding unit; the reducer's
    // weekday base is one unit, so mirror backOutRoundingUnits exactly.
    basePay = backOutRoundingUnits(
      round2(totalHours * input.baseHourlyRate * employmentMultiplier),
      [
        {
          exact: totalHours * input.baseHourlyRate * employmentMultiplier,
          removal: otHours * input.baseHourlyRate * employmentMultiplier,
        },
      ],
    );
  }

  if (!dailyPair && input.shiftBand && input.shiftBand !== 'WEEKDAY_DAY') {
    if (!isCoveredShiftLoadingBand(profile, input.shiftBand)) {
      return zeroBreakdown(ebaCertReducerBandUncoveredReason(input.baseAwardCode, input.shiftBand));
    }
    const compiledMultiplier = findPenaltyMultiplier(
      overlay,
      input.shiftBand,
      input.employment,
      input.classificationStream,
    );
    if (compiledMultiplier === null) {
      return zeroBreakdown(`PENALTY_LINE_MISSING:${input.shiftBand}:${input.employment}`);
    }
    shiftLoadingPay = round2(ordinaryHours * input.baseHourlyRate * (compiledMultiplier - 1));
    coveredBands.add(input.shiftBand);
  }

  penaltyPay = round2(penaltyPay + conditionalResult.penaltyPay);
  shiftLoadingPay = round2(shiftLoadingPay + conditionalResult.shiftLoadingPay);
  for (const code of conditionalResult.covered) coveredBands.add(code);

  if (otHours > 0) {
    const usePriorTier =
      dailyPair &&
      (dailyLoading?.premium ?? 0) > 0 &&
      input.dayType === 'weekday' &&
      !input.dayTypeHours?.saturday &&
      !input.dayTypeHours?.sunday &&
      !input.dayTypeHours?.publicHoliday;
    const tier1HoursRemaining = Math.max(
      0,
      2 -
        Math.max(0, input.overtimeInitialTierHoursConsumedEarlierThisShift ?? 0) -
        Math.max(0, usePriorTier ? (input.overtimeInitialTierHoursConsumedEarlierToday ?? 0) : 0),
    );
    otPay = overtimePay(input.baseHourlyRate, otHours, employmentMultiplier, {
      onLoadedRate: overtimeUsesLoadedRate,
      shiftLoadingIsAdditive: dailyPair,
      shiftLoadingPremium: shiftLoadingPremiumForOvertime ?? 0,
      firstTwoHoursMultiplier: overtimeRule?.firstTwoHoursMultiplier,
      afterTwoHoursMultiplier: overtimeRule?.afterTwoHoursMultiplier,
      tier1HoursRemaining,
    });
    if (otHours > 0) coveredBands.add('OVERTIME_FIRST_2H');
    if (otHours > 2) coveredBands.add('OVERTIME_AFTER_2H');
  }

  const automaticAllowancesPay = automaticMealOvertimeAllowancePay(input, otHours);
  const allowancesPay = round2(allowanceResult.allowancesPay + automaticAllowancesPay);
  const grossTotal = sumMoney(basePay, penaltyPay, shiftLoadingPay, otPay, allowancesPay);

  return {
    basePay,
    penaltyPay,
    overtimePay: otPay,
    shiftLoadingPay,
    allowancesPay,
    grossTotal,
    coveredBands: sortBands(coveredBands),
  };
}
